import { FlashList } from '@shopify/flash-list';
import { format, subYears } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dimensions, Pressable, ScrollView, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Reservation } from '@/api';
import { useDashboardMetrics } from '@/api/partners/dashboard/use-dashboard-metrics';
import type { Filter } from '@/api/partners/reservations/use-reservation-filters';
import {
  FilterName,
  useReservationFilters,
} from '@/api/partners/reservations/use-reservation-filters';
import { useReservations } from '@/api/partners/reservations/use-reservations';
import { useSubscriptions } from '@/api/partners/subscriptions/use-subscriptions';
import { NoCarsEmptyState } from '@/components/first-car-empty-state';
import { useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useDebounce } from '@/core/hooks/use-debounced-value';
import { useHasOrganization } from '@/core/hooks/use-has-organization';
import { isDesktop, titleize, toDateOnlyString } from '@/core/utils';
import { Button, Card, Input, Text } from '@/ui';
import ButtonFilter from '@/ui/button-filter';
import { Calendar } from '@/ui/calendar';
import { Chip, ChipType } from '@/ui/chip';
import DataTable from '@/ui/data-table';
import { Icon } from '@/ui/icon';
import { Person, Search } from '@/ui/icons';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { Dot } from '@/ui/icons/dot';
import { Location } from '@/ui/icons/location';
import { TrashFill } from '@/ui/icons/trash-fill';
import { Loader } from '@/ui/loader';

import { CustomerTypeChip } from './customer-type-chip';
import { DeleteBlockModal } from './delete-block-modal';
import { IdLink, PickupTypeChip, PlateVinCell } from './reservation-parts';
import {
  getPartnerReservationStatusChipType,
  getPartnerReservationStatusLabel,
  getPartnerSubscriptionStatusLabel,
  PARTNER_SUBSCRIPTION_STATUSES,
} from './status-label';
import { SubscriptionCard } from './subscription-card';
import { SubscriptionTable } from './subscription-table';

const PAGE_HEIGHT = Dimensions.get('window').height;
const ROWS_PER_PAGE = 10;

enum Tab {
  DAILY = 'daily',
  SUBSCRIPTION = 'subscription',
}

const MobileListSeparator = () => <View style={{ height: 16 }} />;

const parseFilterValues = (filters: Filter[]) => {
  const locationIdx = filters.findIndex((f) => f.name === FilterName.LOCATION);
  const locationIds = locationIdx > -1 ? filters[locationIdx].selected : [];
  const selectedLocations = filters[locationIdx]?.options
    .filter((f, idx) => locationIds.includes(idx))
    .map((f) => f.value);

  const statusIdx = filters.findIndex((f) => f.name === FilterName.STATUS);
  const statusIds = statusIdx > -1 ? filters[statusIdx].selected : [];
  const selectedStatuses = filters[statusIdx]?.options
    .filter((f, idx) => statusIds.includes(idx))
    .flatMap((f) => (Array.isArray(f.value) ? f.value : [f.value]));

  return {
    locationIds: selectedLocations,
    statuses: selectedStatuses,
  };
};

const mapPartnerStatusFilterOptions = (filter: Filter): Filter => {
  if (filter.name !== FilterName.STATUS) {
    return filter;
  }

  const groupedOptions = filter.options.reduce((acc, option) => {
    const optionName = String(option.name ?? '').toLowerCase();
    const mappedLabel = getPartnerReservationStatusLabel(optionName);

    if (!acc[mappedLabel]) {
      acc[mappedLabel] = {
        ...option,
        name: mappedLabel,
        valueSet: new Set([option.value]),
      };

      return acc;
    }
    acc[mappedLabel].valueSet.add(option.value);
    return acc;
  }, {} as Record<string, any>);

  return {
    ...filter,
    options: Object.values(groupedOptions as Record<string, any>).map(
      ({ valueSet, ...option }: any) => ({
        ...option,
        value: Array.from(valueSet),
      })
    ),
    selected: [],
  };
};

const mapSubscriptionStatusFilterOptions = (filter: Filter): Filter => {
  if (filter.name !== FilterName.STATUS) {
    return filter;
  }

  return {
    ...filter,
    options: PARTNER_SUBSCRIPTION_STATUSES.map((s) => ({
      name: getPartnerSubscriptionStatusLabel(s),
      value: s,
    })),
    selected: [],
  };
};

export default function Reservations() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const status = useAuth.use.status();
  const { hasOrganization, isEVFMC } = useHasOrganization();
  // The tab lives in the URL so returning from a subscription's detail page
  // (or reloading) lands on the Subscription tab again.
  const { tab: tabParam } = useLocalSearchParams<{ tab?: Tab }>();
  const activeTab =
    tabParam === Tab.SUBSCRIPTION ? Tab.SUBSCRIPTION : Tab.DAILY;
  const isSubscriptionTab = activeTab === Tab.SUBSCRIPTION;
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDatetime, setStartDatetime] = useState<Date | null>(null);
  const [endDatetime, setEndDatetime] = useState<Date | null>(null);
  const [filterText, setFilterText] = useState('');
  const debouncedText = useDebounce(filterText, 400);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [tempFilters, setTempFilters] = useState<Filter[]>([]);
  const [activeFilterIdx, setActiveFilterIdx] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [showDeleteBlockModal, setShowDeleteBlockModal] = useState(false);
  const [activeTrip, setActiveTrip] = useState<Reservation | null>(null);
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
  const { data: dashboardMetricsData, isLoading: isLoadingDashboardMetrics } =
    useDashboardMetrics({});
  const [showAdminBlocksOnly, setShowAdminBlocksOnly] = useState(false);

  const selectedFilterValues = useMemo(
    () => parseFilterValues(filters),
    [filters]
  );
  const { data: filtersFromApi, refetch: refetchReservationFilters } =
    useReservationFilters({
      variables: { text: debouncedText },
    });

  // Only the active tab's list query runs; the other tab is fetched on switch.
  const reservationsQuery = useReservations({
    enabled: status === 'signIn' && !isSubscriptionTab,
    rentalType: 'daily',
    isBlock: showAdminBlocksOnly ? true : undefined,
    text: debouncedText,
    startDatetime: toDateOnlyString(startDatetime),
    endDatetime: toDateOnlyString(endDatetime),
    locationIds: selectedFilterValues.locationIds,
    statuses: showAdminBlocksOnly ? undefined : selectedFilterValues.statuses,
  });
  const subscriptionsQuery = useSubscriptions({
    enabled: status === 'signIn' && isSubscriptionTab,
    text: debouncedText,
    startDatetime: toDateOnlyString(startDatetime),
    endDatetime: toDateOnlyString(endDatetime),
    locationIds: selectedFilterValues.locationIds,
    statuses: selectedFilterValues.statuses,
  });
  // Unfiltered single-row queries that only supply the tab counts and the
  // "no reservations at all" empty state, so they ignore search and filters.
  const dailyCountQuery = useReservations({
    enabled: status === 'signIn',
    rentalType: 'daily',
    perPage: 1,
  });
  const subscriptionCountQuery = useSubscriptions({
    enabled: status === 'signIn',
    perPage: 1,
  });

  const activeQuery = isSubscriptionTab
    ? subscriptionsQuery
    : reservationsQuery;
  const { data: reservationsData } = reservationsQuery;
  const { data: subscriptionsData } = subscriptionsQuery;
  const activeFirstPage = activeQuery.data?.pages[0];
  // Loading and paging state always follows the active tab's query.
  const {
    isFetchingNextPage,
    fetchNextPage,
    isPending,
    hasNextPage,
    refetch: refetchActiveQuery,
  } = activeQuery;
  const dailyCount = dailyCountQuery.data?.pages[0]?.meta?.totalCount;
  const subscriptionCount =
    subscriptionCountQuery.data?.pages[0]?.meta?.totalCount;

  const reservations = useMemo(() => {
    return reservationsData?.pages.map((queryPage) => queryPage.data).flat();
  }, [reservationsData]);
  const subscriptions = useMemo(() => {
    return subscriptionsData?.pages.map((queryPage) => queryPage.data).flat();
  }, [subscriptionsData]);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Reservations',
      Tab: isSubscriptionTab ? 'Subscription' : 'Daily rental',
    });
  }, [isSubscriptionTab]);

  useEffect(() => {
    refetchReservationFilters();
  }, [debouncedText]);

  // The filters API only returns reservation statuses, so the subscription tab
  // swaps in its own status options.
  const getTabFilters = (tab: Tab) =>
    (filtersFromApi || []).map(
      tab === Tab.SUBSCRIPTION
        ? mapSubscriptionStatusFilterOptions
        : mapPartnerStatusFilterOptions
    );

  const handleTabChange = (newTab: Tab) => {
    // Filters are tab-specific (different status options), so start fresh.
    router.setParams({ tab: newTab });
    setShowAdminBlocksOnly(false);
    setActiveFilterIdx(null);
    setTempFilters([]);
    setFilters(getTabFilters(newTab));
  };

  useEffect(() => {
    setFilters(getTabFilters(activeTab));
  }, [filtersFromApi]);

  useEffect(() => {
    if (!hasCompletedInitialLoad && !isPending && reservationsData) {
      setHasCompletedInitialLoad(true);
    }
  }, [hasCompletedInitialLoad, isPending, reservationsData]);

  const selectedFiltersKey = useMemo(
    () =>
      JSON.stringify(
        filters.map((filter) => ({
          name: filter.name,
          selected: filter.selected,
        }))
      ),
    [filters]
  );

  const countsSettled =
    dailyCount != null &&
    dailyCount >= 0 &&
    subscriptionCount != null &&
    subscriptionCount >= 0 &&
    !dailyCountQuery.isFetching &&
    !subscriptionCountQuery.isFetching;
  const hasNoCars =
    !isLoadingDashboardMetrics &&
    (dashboardMetricsData?.totalCarsCount ?? 0) === 0;
  const hasAnyRegularFilterActive =
    debouncedText.trim().length > 0 ||
    startDatetime !== null ||
    endDatetime !== null ||
    filters.some(
      (filter) => Array.isArray(filter.selected) && filter.selected.length > 0
    );
  const hasActiveFilters = hasAnyRegularFilterActive || showAdminBlocksOnly;
  const globalReservationCountEmpty =
    countsSettled && dailyCount + subscriptionCount === 0;
  const activeListHasReservations =
    (activeFirstPage?.meta?.totalCount ?? 0) > 0 ||
    (activeFirstPage?.data?.length ?? 0) > 0;
  const showNoReservationsEmpty =
    !hasActiveFilters &&
    globalReservationCountEmpty &&
    activeQuery.isSuccess &&
    !activeQuery.isFetching &&
    !activeListHasReservations;

  useEffect(() => {
    setPage(0);
  }, [
    debouncedText,
    startDatetime,
    endDatetime,
    selectedFiltersKey,
    showAdminBlocksOnly,
    activeTab,
  ]);

  const getReservations = useCallback(
    (useMobileSource: boolean): Reservation[] => {
      if (useMobileSource) {
        return reservations ?? [];
      }

      return reservationsData?.pages[page]?.data ?? [];
    },
    [reservations, reservationsData, page]
  );

  const filterReservations = useCallback(
    (reservationList: Reservation[]): Reservation[] => {
      if (showAdminBlocksOnly) {
        return reservationList;
      }

      return reservationList.filter((reservation) => !reservation.isBlock);
    },
    [showAdminBlocksOnly]
  );

  const desktopPageReservations = useMemo(() => {
    return filterReservations(getReservations(false));
  }, [getReservations, filterReservations]);

  const mobileReservations = useMemo(() => {
    return filterReservations(getReservations(true));
  }, [getReservations, filterReservations]);

  const desktopPageSubscriptions = subscriptionsData?.pages[page]?.data ?? [];
  const mobileSubscriptions = subscriptions ?? [];

  const reservationRows = useCallback(
    (reservationList: Reservation[]) => {
      if (!reservationList) return [];

      return reservationList?.map((trip) => ({
        key: trip.id,
        id: trip.id,
        'reservation-id': trip.isBlock ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text type="bodyRegular">{trip.id}</Text>

            <Pressable
              hitSlop={8}
              onPress={() => {
                setActiveTrip(trip);
                setShowDeleteBlockModal(true);
              }}
            >
              <Icon
                type="naked"
                icon={<TrashFill />}
                fillColor={theme.colors.icon.color}
                width={12}
                height={12}
              />
            </Pressable>
          </View>
        ) : (
          <IdLink id={trip.id} href={`/partner/reservations/${trip.id}`} />
        ),
        'car-details': (
          <PlateVinCell plate={trip.car.licensePlate} vin={trip.car.vin} />
        ),
        'pickup-time': (
          <View>
            <Text type="caption1">
              {DateTime.fromISO(trip.startTime, {
                zone: trip.pickupLocation.timezone,
              }).toFormat('MM/dd/yyyy - hh:mm a ZZZZ')}
            </Text>
          </View>
        ),
        'dropoff-time': (
          <View>
            <Text type="caption1">
              {DateTime.fromISO(trip.endTime, {
                zone: trip.pickupLocation.timezone,
              }).toFormat('MM/dd/yyyy - hh:mm a ZZZZ')}
            </Text>
          </View>
        ),
        status: (
          <Chip
            type={
              trip.isBlock
                ? ChipType.DEFAULT
                : getPartnerReservationStatusChipType(trip.status)
            }
            icon={<Dot />}
            text={getPartnerReservationStatusLabel(trip.status, trip.isBlock)}
            iconWidth={8}
            iconHeight={8}
            style={{
              alignSelf: 'center',
              paddingHorizontal: 8.5,
            }}
          />
        ),
        'pickup-delivery-address': trip.isBlock ? (
          <Text>-</Text>
        ) : (
          <View style={{ gap: 8 }}>
            <Text type="caption1" style={{ textDecorationLine: 'underline' }}>
              {trip.customPickupLocation || trip.pickupLocation.address}
            </Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {trip.customPickupLocation
                ? 'Delivery address'
                : 'Pickup address'}
            </Text>
          </View>
        ),
        customer: (
          <View>
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {/* EonOne reservations always show the full customer name. */}
              {trip.source !== 'eon_one' &&
              ['open', 'verified', 'late', 'cancelled'].includes(trip.status)
                ? `${trip.customerFirstname} ${
                    trip.customerLastname?.[0] ?? ''
                  }.`
                : trip.customerName}
            </Text>
          </View>
        ),
        'rental-type': trip.isBlock ? (
          <Chip text="Admin Block" />
        ) : (
          <PickupTypeChip isDelivery={!!trip.customPickupLocation} />
        ),
        'customer-type':
          trip.isBlock || !trip.customerType ? (
            <Text>-</Text>
          ) : (
            <CustomerTypeChip type={trip.customerType} />
          ),
      }));
    },
    [reservations]
  );

  const handlePageChange = (p: number) => {
    const currentPage = (
      isSubscriptionTab ? subscriptionsData : reservationsData
    )?.pages.at(-1)?.meta.page;

    if (currentPage !== undefined && p > currentPage - 1) {
      fetchNextPage();
    }

    setPage(p);
  };

  const mobileListProps = {
    ItemSeparatorComponent: MobileListSeparator,
    onEndReached: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    onRefresh: async () => {
      setRefreshing(true);

      await refetchActiveQuery();

      setRefreshing(false);
    },
    refreshing,
    estimatedItemSize: 20,
    showsVerticalScrollIndicator: false,
    ListFooterComponent: isFetchingNextPage ? (
      <Loader style={{ height: 140 }} />
    ) : null,
  };

  if (isPending && !hasCompletedInitialLoad) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  if (hasNoCars) {
    return (
      <NoCarsEmptyState
        title="No Reservations Yet"
        body="Your reservations will show up here once someone books your car"
        ctaText="Add Your First Car"
        redirectPath="partner/cars/setup?fromSf=1"
      />
    );
  }

  if (showNoReservationsEmpty) {
    return (
      <View
        style={{
          backgroundColor: theme.colors.background_1,
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <View style={{ padding: 16, alignItems: 'center', gap: 16 }}>
          <Icon
            type="dressed"
            icon={<CalendarIcon />}
            fillColor={theme.colors.icon.color}
            width={16}
            height={16}
            style={{
              borderRadius: 28,
            }}
          />

          <View style={{ alignItems: 'center', gap: 8 }}>
            <Text type="title2">No Reservations Yet</Text>

            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textAlign: 'center',
              }}
            >
              Your reservations will show up here once someone books your car
            </Text>
          </View>

          {isEVFMC ? (
            <Button
              text="Create Reservation"
              type="primarySmall"
              onPress={() => {
                router.push('/partner/reservations/new');
              }}
              style={{ width: 'auto' }}
            />
          ) : null}
        </View>
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
        maxHeight: PAGE_HEIGHT,
      }}
    >
      <View style={styles.container}>
        <View style={styles.nav}>
          <Text type="title1">Reservations</Text>

          {hasOrganization ? (
            <Button
              text="Create Reservation"
              type="primarySmall"
              onPress={() => {
                router.push('/partner/reservations/new');
              }}
              style={{ width: 'auto' }}
            />
          ) : null}
        </View>

        <View style={{ gap: 24, flex: 1 }}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Button
              text={`Daily Rental${
                dailyCount === undefined ? '' : ` (${dailyCount})`
              }`}
              type={isSubscriptionTab ? 'secondarySmall' : 'primarySmall'}
              onPress={() => {
                handleTabChange(Tab.DAILY);
              }}
              style={{ width: 'auto' }}
            />

            <Button
              text={`Subscription${
                subscriptionCount === undefined ? '' : ` (${subscriptionCount})`
              }`}
              type={isSubscriptionTab ? 'primarySmall' : 'secondarySmall'}
              onPress={() => {
                handleTabChange(Tab.SUBSCRIPTION);
              }}
              style={{ width: 'auto' }}
            />
          </View>

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              zIndex: 1,
            }}
          >
            <View style={{ flex: 1, gap: 16, zIndex: 1 }}>
              <View style={styles.searchAndFilters}>
                <Input
                  placeholder="Search by ID, VIN, license, etc."
                  value={filterText}
                  onChangeText={(t: string) => setFilterText(t)}
                  leftIcon={
                    <Icon
                      type="naked"
                      icon={<Search />}
                      fillColor={theme.colors.icon.color}
                      width={22}
                      height={22}
                    />
                  }
                  containerStyle={styles.input}
                />

                <ScrollView
                  style={styles.filters}
                  horizontal
                  showsHorizontalScrollIndicator={false}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: 8,
                    }}
                  >
                    {filters && filters.length > 0
                      ? filters.map((filter: Filter, idx) => {
                          const formattedName = titleize(filter.name);

                          if (filter.collapsed) {
                            return null;
                          }

                          return (
                            <ButtonFilter
                              key={filter.name}
                              name={
                                filter.selected.length > 0
                                  ? `${formattedName} (${filter.selected.length})`
                                  : formattedName
                              }
                              headerTitle={formattedName}
                              handlePress={(selectedOptions: number[]) => {
                                const updatedFilters = [
                                  ...filters.slice(0, idx),
                                  {
                                    ...filter,
                                    selected: selectedOptions,
                                  },
                                  ...filters.slice(idx + 1),
                                ];

                                setTempFilters(updatedFilters);
                              }}
                              options={filter.options}
                              onOpen={() => {
                                setActiveFilterIdx(idx);
                              }}
                              onClose={() => {
                                setTempFilters([]);
                                setActiveFilterIdx(null);
                              }}
                              onClearAll={() => {
                                let selected: any[] = [];

                                const updatedFilters = [
                                  ...filters.slice(0, idx),
                                  {
                                    ...filter,
                                    selected,
                                  },
                                  ...filters.slice(idx + 1),
                                ];

                                setFilters(updatedFilters);

                                const updatedTempFilters = [
                                  ...tempFilters.slice(0, idx),
                                  {
                                    ...filter,
                                    selected,
                                  },
                                  ...tempFilters.slice(idx + 1),
                                ];

                                setTempFilters(updatedTempFilters);
                              }}
                              onSave={(selectedOptions: number[]) => {
                                const updatedFilters = [
                                  ...filters.slice(0, idx),
                                  {
                                    ...filter,
                                    selected: selectedOptions,
                                  },
                                  ...filters.slice(idx + 1),
                                ];

                                setFilters(updatedFilters);
                                setActiveFilterIdx(null);
                              }}
                              opened={activeFilterIdx === idx}
                              active={activeFilterIdx === idx}
                              selected={filter.selected}
                            />
                          );
                        })
                      : null}

                    <Card
                      style={{
                        padding: 16,
                        borderRadius: 8,
                        paddingTop: 8,
                        paddingBottom: 8,
                        paddingLeft: 16,
                        paddingRight: 16,
                        borderColor:
                          startDatetime && endDatetime
                            ? theme.colors.buttonFilter.active.border
                            : 'transparent',
                      }}
                    >
                      <Pressable
                        onPress={() => {
                          setShowCalendar(true);
                        }}
                      >
                        <Text type="bodyRegular">Date range</Text>
                      </Pressable>
                    </Card>

                    {isSubscriptionTab ? null : (
                      <Card
                        style={{
                          padding: 16,
                          borderRadius: 8,
                          paddingTop: 8,
                          paddingBottom: 8,
                          paddingLeft: 16,
                          paddingRight: 16,
                          borderColor: showAdminBlocksOnly
                            ? theme.colors.buttonFilter.active.border
                            : 'transparent',
                        }}
                      >
                        <Pressable
                          onPress={() => {
                            setShowAdminBlocksOnly((current) => !current);
                          }}
                        >
                          <Text type="bodyRegular">Admin Blocks</Text>
                        </Pressable>
                      </Card>
                    )}
                  </View>
                </ScrollView>
              </View>

              <View style={{ gap: 16, zIndex: 1 }}>
                <Calendar
                  mode="date"
                  isVisible={showCalendar}
                  headerTitle={'Date'}
                  minDate={subYears(new Date(), 2)}
                  defaultStartDate={startDatetime}
                  defaultEndDate={endDatetime}
                  onHide={() => {
                    setShowCalendar(false);
                  }}
                  onClear={() => {
                    setStartDatetime(null);
                    setEndDatetime(null);
                    setShowCalendar(false);
                  }}
                  onDatesSelected={(startDt, endDt) => {
                    setStartDatetime(startDt);
                    setEndDatetime(endDt);
                    setShowCalendar(false);
                  }}
                  range
                  style={{
                    zIndex: 1,
                    position: 'absolute',
                    top: -75,
                    left: 100,
                  }}
                  containerStyle={{
                    borderWidth: 1,
                    borderColor: theme.colors.card.default.borderColor,
                    shadowColor: theme.colors.card.default.borderColor,
                    shadowOffset: {
                      width: 0,
                      height: 5,
                    },
                    shadowOpacity: 0.5,
                    shadowRadius: 5,
                    elevation: 10,
                    height: '100%',
                  }}
                  id="reservations-calendar"
                  hasBackdrop
                  coverScreen
                  showDesktopOverview
                />
              </View>
            </View>
          </View>

          <View style={styles.reservationsContainer}>
            {isPending && !isFetchingNextPage ? (
              <View
                style={{
                  width: '100%',
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: theme.colors.background_1,
                }}
              >
                <Loader style={{ height: 250, width: 250 }} />
              </View>
            ) : isDesktop() && isSubscriptionTab ? (
              <SubscriptionTable
                subscriptions={desktopPageSubscriptions}
                page={page}
                total={subscriptionsData?.pages[0].meta.totalCount}
                rowsPerPage={ROWS_PER_PAGE}
                onPageChange={handlePageChange}
              />
            ) : isDesktop() ? (
              <DataTable
                columns={[
                  {
                    key: 'reservation-id',
                    title: 'ID',
                    style: { minWidth: 110 },
                  },
                  {
                    key: 'customer',
                    title: 'CUSTOMER',
                    style: { minWidth: 160 },
                  },
                  { key: 'status', title: 'STATUS', style: { minWidth: 150 } },
                  {
                    key: 'pickup-time',
                    title: 'START TIME',
                    style: { minWidth: 190 },
                  },
                  {
                    key: 'dropoff-time',
                    title: 'END TIME',
                    style: { minWidth: 190 },
                  },
                  {
                    key: 'pickup-delivery-address',
                    title: 'ADDRESS',
                    style: { minWidth: 360 },
                  },
                  {
                    key: 'rental-type',
                    title: 'RENTAL TYPE',
                    style: { minWidth: 140 },
                  },
                  {
                    key: 'car-details',
                    title: 'PLATE | VIN',
                    style: { minWidth: 180 },
                  },
                  {
                    key: 'customer-type',
                    title: 'CUSTOMER TYPE',
                    style: { minWidth: 160 },
                  },
                ]}
                rows={reservationRows(desktopPageReservations)}
                page={page}
                total={reservationsData?.pages[0].meta.totalCount}
                rowsPerPage={ROWS_PER_PAGE}
                onPageChange={handlePageChange}
                stickToBottom
              />
            ) : isSubscriptionTab ? (
              <FlashList
                data={mobileSubscriptions}
                renderItem={({ item }) => (
                  <SubscriptionCard subscription={item} />
                )}
                {...mobileListProps}
              />
            ) : (
              <FlashList
                data={mobileReservations}
                renderItem={({ item }) => (
                  <Pressable
                    disabled={item.isBlock}
                    onPress={() => {
                      router.push(`/partner/reservations/${item.id}`);
                    }}
                  >
                    <Card>
                      <View
                        style={{
                          padding: 16,
                          borderBottomWidth: 1,
                          borderBottomColor:
                            theme.colors.card.default.borderColor,
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <View style={{ gap: 16 }}>
                          <Text type="title2">{`${item.car.vehicleYear} ${item.car.brandName} ${item.car.modelName} (${item.car.licensePlate})`}</Text>

                          {item.isBlock ? (
                            <View>
                              <Text
                                type="caption1"
                                style={{ color: theme.colors.text.secondary }}
                              >
                                {getPartnerReservationStatusLabel(
                                  item.status,
                                  item.isBlock
                                )}
                              </Text>
                            </View>
                          ) : (
                            <View
                              style={{
                                flexDirection: 'row',
                                gap: 8,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Chip
                                type={getPartnerReservationStatusChipType(
                                  item.status
                                )}
                                icon={<Dot />}
                                text={getPartnerReservationStatusLabel(
                                  item.status
                                )}
                                iconWidth={8}
                                iconHeight={8}
                              />

                              <PickupTypeChip
                                isDelivery={!!item.customPickupLocation}
                              />

                              <Chip text={item.car.vin} />
                            </View>
                          )}
                        </View>
                      </View>

                      <View
                        style={{
                          flex: 1,
                          padding: 16,
                          gap: 16,
                        }}
                      >
                        <View style={{ gap: 8, flexDirection: 'row' }}>
                          <View>
                            <Icon
                              type="naked"
                              icon={<Person />}
                              fillColor={theme.colors.icon.color}
                              width={16}
                              height={16}
                              style={{
                                width: 40,
                                height: 40,
                              }}
                            />
                          </View>

                          <View>
                            <Text type="bodyRegular">Customer</Text>

                            <Text
                              type="bodyRegular"
                              style={{ color: theme.colors.text.secondary }}
                            >
                              {[
                                'open',
                                'verified',
                                'late',
                                'cancelled',
                              ].includes(item.status)
                                ? `${item.customerFirstname} ${
                                    item.customerLastname?.[0] ?? ''
                                  }.`
                                : item.customerName}
                            </Text>
                          </View>
                        </View>

                        <View style={{ gap: 8, flexDirection: 'row' }}>
                          <View>
                            <Icon
                              type="naked"
                              icon={<Location />}
                              fillColor={theme.colors.icon.color}
                              width={16}
                              height={16}
                              style={{
                                width: 40,
                                height: 40,
                              }}
                            />
                          </View>

                          <View style={{ flex: 1 }}>
                            <Text type="bodyRegular">
                              {item.isBlock
                                ? 'Start'
                                : t(
                                    item.customPickupLocation &&
                                      item.customReturnLocation
                                      ? 'carId.deliveryLocation'
                                      : 'carId.pickupLocation'
                                  )}
                            </Text>

                            <Text
                              type="bodyRegular"
                              style={{ color: theme.colors.text.secondary }}
                            >
                              {format(
                                toZonedTime(
                                  item.startTime,
                                  item.pickupLocation.timezone
                                ),
                                'MMM dd yyyy - hh:mm a'
                              )}
                            </Text>

                            {!item.isBlock ? (
                              <Text
                                type="bodyRegular"
                                style={{ color: theme.colors.text.secondary }}
                              >
                                {item.customPickupLocation ||
                                  item.pickupLocation.address}
                              </Text>
                            ) : null}
                          </View>
                        </View>

                        <View style={{ gap: 8, flexDirection: 'row' }}>
                          <View>
                            <Icon
                              type="naked"
                              icon={<Location />}
                              fillColor={theme.colors.icon.color}
                              width={16}
                              height={16}
                              style={{
                                width: 40,
                                height: 40,
                              }}
                            />
                          </View>

                          <View style={{ flex: 1 }}>
                            <Text type="bodyBold">
                              {item.isBlock ? 'End' : t('carId.returnLocation')}
                            </Text>

                            <Text
                              type="bodyRegular"
                              style={{ color: theme.colors.text.secondary }}
                            >
                              {format(
                                toZonedTime(
                                  item.endTime,
                                  item.returnLocation.timezone
                                ),
                                'MMM dd yyyy - hh:mm a'
                              )}
                            </Text>

                            {!item.isBlock ? (
                              <Text
                                type="bodyRegular"
                                style={{ color: theme.colors.text.secondary }}
                              >
                                {item.customReturnLocation ||
                                  item.returnLocation.address}
                              </Text>
                            ) : null}
                          </View>
                        </View>
                      </View>

                      {item.isBlock ? (
                        <View
                          style={{
                            borderTopWidth: 1,
                            borderTopColor:
                              theme.colors.card.default.borderColor,
                            padding: 16,
                          }}
                        >
                          <Button
                            text="Delete Admin Block"
                            type="ghost"
                            textStyle={{
                              color: theme.colors.error,
                            }}
                            onPress={() => {
                              setActiveTrip(item);
                              setShowDeleteBlockModal(true);
                            }}
                          />
                        </View>
                      ) : null}
                    </Card>
                  </Pressable>
                )}
                {...mobileListProps}
              />
            )}
          </View>
        </View>
      </View>

      <DeleteBlockModal
        visible={showDeleteBlockModal}
        trip={activeTrip}
        onClose={() => {
          setShowDeleteBlockModal(false);
        }}
        onSuccess={() => {
          Toast.show({
            text1: 'Block deleted successfully',
            type: 'success',
          });
          setShowDeleteBlockModal(false);
          reservationsQuery.refetch();
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((_theme) => ({
  container: {
    flexGrow: 1,
    borderRadius: 16,
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
    maxHeight: '100%',
    gap: 56,
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  // Search and filters share a row on desktop and stack on mobile.
  searchAndFilters: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      md: 'center',
    },
    gap: {
      xs: 16,
      md: 24,
    },
    zIndex: 1,
  },
  input: {
    width: {
      md: 400,
    },
    // The base input grows to fill its row; keep the search at a fixed width.
    flexGrow: 0,
  },
  filters: {
    flexGrow: 0,
    // Lets the chips scroll sideways instead of overflowing the row.
    flexShrink: {
      xs: 0,
      md: 1,
    },
  },
  reservationsContainer: {
    flex: {
      xs: 1,
      md: undefined,
    },
  },
}));
